import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    IconButton,
} from "@mui/material";
import {
    Close as CloseIcon,
    Lock as LockIcon,
} from "@mui/icons-material";

interface PasswordModalProps {
    open: boolean;
    onClose: () => void;
    onPasswordSubmit: (password: string) => void;
    title: string;
    description?: string;
    isLoading?: boolean;
    error?: string | null;
}

const PasswordModal: React.FC<PasswordModalProps> = ({
    open,
    onClose,
    onPasswordSubmit,
    title,
    description,
    isLoading = false,
    error = null,
}) => {
    const [password, setPassword] = useState("");

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (password.trim()) {
            onPasswordSubmit(password.trim());
        }
    };

    const handleClose = () => {
        setPassword("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pb: 1
            }}>
                <LockIcon color="primary" />
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    {title}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                {description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {description}
                    </Typography>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Enter VIN"
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                        placeholder="Enter the vehicle's VIN number"
                        variant="outlined"
                        sx={{ mb: 2 }}
                        inputProps={{
                            style: { textTransform: 'uppercase' }
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    onClick={handleClose}
                    disabled={isLoading}
                    color="inherit"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isLoading || !password.trim()}
                    type="submit"
                >
                    {isLoading ? "Verifying..." : "Access Documents"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PasswordModal;
